'use client';
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { createClientResourceCache } from '@/lib/client-resource-cache';
import { socialApi, useSocial } from './client';

export function useSharedSocialResource<T>(url: string | null) {
  const { profile, loading, resourceCache } = useSocial(), identity = profile?.id || 'anonymous';
  const cache = useMemo(() => resourceCache || createClientResourceCache<unknown>(identity, 120000, undefined, socialApi), [resourceCache, identity]);
  const snapshot = useSyncExternalStore(cache.subscribe, cache.getSnapshot, cache.getServerSnapshot);
  useEffect(() => { if (!resourceCache) return () => cache.dispose(); }, [cache, resourceCache]);
  useEffect(() => {
    if (!url || loading) return;
    void cache.load(url);
    const update = () => { if (!document.hidden) void cache.load(url); };
    const timer = setInterval(update, 120000);
    window.addEventListener('focus', update); document.addEventListener('visibilitychange', update);
    const unsubscribe = cache.subscribe(() => { if (!cache.getSnapshot().entries.has(url)) void cache.load(url); });
    return () => { clearInterval(timer); window.removeEventListener('focus', update); document.removeEventListener('visibilitychange', update); unsubscribe(); };
  }, [cache, url, loading]);
  const entry = url ? snapshot.entries.get(url) : undefined;
  return {
    data: entry?.data as T | undefined, error: entry?.error,
    loading: Boolean(url && !entry?.data && !entry?.error), refreshing: Boolean(entry?.data && entry.loading),
    reload: () => { if (url) void cache.load(url, true); },
    update: (data: T | ((current: T) => T)) => { if (!url) return; const current = cache.getSnapshot().entries.get(url)?.data as T | undefined; if (current !== undefined) cache.update(url, typeof data === 'function' ? (data as (value: T) => T)(current) : data, current); },
  };
}
